import { NEW_APT_STEPS } from 'constant';
import { useHistory } from 'react-router-dom';
import DateStaff from './date-staff';
import useNewAppointmentHook from './hooks/index.hook';
import Information from './information';
import Review from './review';
import Service from './service';
import Success from './success';
import TimeSlot from './time-slot';
import React, { createRef, useEffect } from 'react';
import AptContext from './hooks/index.context';
import Cookies from 'js-cookie';

export interface StateNewAppointmentRef {
  isBackToReview: boolean;
}

// createRef() types `.current` as readonly, but this ref is intentionally
// mutated directly outside of React's ref-callback lifecycle (see below and
// components/new-appointment/Header.tsx); typed as a plain mutable holder
// to keep that pattern working.
export let stateNewAppointmentRef: { current: StateNewAppointmentRef | null } = createRef();
// createRef() takes no arguments; this pre-existing call passes one that
// React ignores at runtime. Cast keeps the call (and its dead argument) as-is.
export let appointmentPath = (createRef as any)('/new-appointment/');

export interface NewAppointmentViewProps {
  go: (step: string) => void;
  rollbackTo: (step: string) => void;
  back: () => void;
  home: () => void;
}

interface NewAppointmentContextProps {
  children?: React.ReactNode;
  isAdmin?: boolean;
}

export const NewAppointmentContext = ({ children, isAdmin = false }: NewAppointmentContextProps) => {
  const hook = useNewAppointmentHook();

  useEffect(() => {
    stateNewAppointmentRef.current = {
      isBackToReview: false,
    }
  }, []);

  return (
    <AptContext.Provider value={hook.context}>
      {children}
    </AptContext.Provider>
  )
};

interface NewAppointmentContainerProps {
  View: React.ComponentType<NewAppointmentViewProps>;
}

const NewAppointmentContainer = ({ View }: NewAppointmentContainerProps) => {
  const _history = useHistory();
  const getPath = (step?: string) => {
    switch (step) {
      case NEW_APT_STEPS.SUCCESS: return 'success';
      case NEW_APT_STEPS.SERVICE: return 'service';
      case NEW_APT_STEPS.DATE_STAFF: return 'date-staff';
      case NEW_APT_STEPS.TIME_SLOT: return 'time-slot';
      case NEW_APT_STEPS.REVIEW: return 'review';
      case NEW_APT_STEPS.INFORMATION:
      default: return '';
    }
  };

  const go = (step: string) => {
    let path = getPath(step);
    if (stateNewAppointmentRef.current)
      stateNewAppointmentRef.current.isBackToReview = false;
    _history.push('/new-appointment/' + path, undefined);
  };

  const rollbackTo = (step: string) => {
    let path = getPath(step);
    if (stateNewAppointmentRef.current)
      stateNewAppointmentRef.current.isBackToReview = true;
    _history.push('/new-appointment/' + path);
  };
  const back = () => _history.goBack();

  const home = () => {
    if (!!+(Cookies.get('isManager') as string)) {
      return _history.push('/appointments');
    }
    return _history.push('/');
  };

  return (
    <div className='new-appointment-wrapper'>
      <View
        go={go}
        rollbackTo={rollbackTo}
        back={back}
        home={home}
      />
    </div>
  );
};

const getNewAptRoute = (step?: string, admin?: boolean) => {
  let View: React.ComponentType<NewAppointmentViewProps> = () => null;
  let path = '';
  switch (step) {
    case NEW_APT_STEPS.SUCCESS: {
      View = Success;
      path = 'success';
      break;
    }
    case NEW_APT_STEPS.SERVICE: {
      View = Service;
      path = 'service';
      break;
    }
    case NEW_APT_STEPS.DATE_STAFF: {
      View = DateStaff;
      path = 'date-staff';
      break;
    }
    case NEW_APT_STEPS.TIME_SLOT: {
      View = TimeSlot;
      path = 'time-slot';
      break;
    }
    case NEW_APT_STEPS.REVIEW: {
      View = Review;
      path = 'review';
      break;
    }
    case NEW_APT_STEPS.INFORMATION:
    default:
      View = Information;
      path = '';
  }

  const component = () => <NewAppointmentContainer View={View} />;
  return ({
    path: '/new-appointment/' + path,
    name: 'New Appointment',
    component,
    layout: 'admin',
    isDisplayHeader: false,
    isDisplayNav: false,
    isManager: !!+(Cookies.get('isManager') as string),
  })
}

export default getNewAptRoute;
